import { DlfMatcherTypes } from "../../types/rules/DlfMatcherTypes";
import Span from "../Span";
import {
  AllowEmptyDlfMatcher,
  AllowEmptyDlfMatcherProperties,
} from "./AllowEmptyDlfMatcher";
import { DlfMatcher, DlfMatcherProperties } from "./DlfMatcher";
import { MatcherFactory } from "./factory/MatcherFactory";
import { SectionFactory } from "./factory/SectionFactory";
import { FuzzyFieldMatcher } from "./FuzzyFieldMatcher";
import { PageSection } from "./PageSection";
import { RegexFieldMatcher } from "./RegexFieldMatcher";
import { Section, SectionData } from "./Section";

type SectionMatcherData = {
  section: SectionData;
  matcher: DlfMatcherProperties;
} & AllowEmptyDlfMatcherProperties;

class SectionMatcher
  extends AllowEmptyDlfMatcher
  implements SectionMatcherData
{
  discriminator: DlfMatcherTypes = "F_SECTION";
  /* SectionMatcher properties */
  section: Section;
  matcher: DlfMatcher;
  /* Allow Empty Matcher properties */
  allowEmpty: boolean;
  /* Dlf Matcher property */
  fieldName: string;
  /* Matcher properties */
  position: number;
  reusable: boolean;
  threshold: number;
  ocrConfidenceThreshold: number;

  constructor(data: SectionMatcherData) {
    super();
    this.section = SectionFactory.createSection(data.section);
    this.matcher = MatcherFactory.createDlfMatcher(data.matcher);
    this.fieldName = data.fieldName;
    this.matcher.fieldName = this.fieldName;
    this.position = data.position;
    this.reusable = data.reusable ?? true;
    this.allowEmpty = data.allowEmpty ?? false;
    this.threshold = data.threshold;
    this.ocrConfidenceThreshold = data.ocrConfidenceThreshold;
  }

  static calculateCoordsFromSpans(
    spans: Span[],
    pageWidth: number,
    pageHeight: number,
  ) {
    return null;
  }

  setAllowEmpty(value: boolean) {
    this.allowEmpty = value;
    if (this.matcher.supportsAllowEmpty()) {
      (this.matcher as AllowEmptyDlfMatcher).setAllowEmpty(value);
    }
  }
  isConfigured(): boolean {
    return this.section.isConfigured() && this.matcher.isConfigured();
  }
  clear(): void {
    this.matcher.clear();
    this.section.clear();
  }

  setNestedMatcherText(value: string) {
    if (this.matcher.getDiscriminator() === "F_REGEX") {
      (this.matcher as RegexFieldMatcher).regex = value.trim();
    } else if (this.matcher.getDiscriminator() === "F_FUZZY") {
      (this.matcher as FuzzyFieldMatcher).fuzzyKey = value.trim();
    }
  }

  toMatcherData(): SectionMatcherData {
    return {
      ocrConfidenceThreshold: this.ocrConfidenceThreshold,
      discriminator: this.discriminator,
      fieldName: this.fieldName,
      position: this.position,
      reusable: this.reusable,
      allowEmpty: this.allowEmpty,
      matcher: this.matcher.toMatcherData(),
      section: this.section.toSectionData(),
      threshold: this.matcher.threshold,
    };
  }
  getReusable(): boolean {
    return this.reusable;
  }
  setReusable(value: boolean): void {
    this.reusable = value;
  }

  getDiscriminator(): DlfMatcherTypes {
    return this.discriminator;
  }

  getMatcherId(): string {
    // actively ignore threshold
    return `${this.discriminator}-[${this.matcher.getMatcherId()}]-[${this.section.getSectionId()}]`;
  }

  static createTest(rest: Partial<SectionMatcherData> = {}): SectionMatcher {
    const data: SectionMatcherData = {
      ocrConfidenceThreshold: -1,
      discriminator: "F_SECTION",
      fieldName: "",
      matcher: RegexFieldMatcher.createTest({}),
      position: 0,
      reusable: true,
      section: PageSection.createTest({}),
      allowEmpty: false,
      threshold: 0,
    };

    return new SectionMatcher({
      ...data,
      ...rest,
    });
  }
}

export { SectionMatcher, SectionMatcherData };
